/**
 * ClientEstimationPage - Client dashboard for project estimations
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { EstimationService } from '../services/EstimationService';
import { ProjectEstimation, EstimationStatus } from '../types/estimation';
import ProjectSubmissionForm from '../components/estimation/ProjectSubmissionForm';
import ClarificationQA from '../components/estimation/ClarificationQA';
import SpecificationViewer from '../components/estimation/SpecificationViewer';

export const ClientEstimationPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [estimations, setEstimations] = useState<ProjectEstimation[]>([]);
  const [selectedEstimation, setSelectedEstimation] =
    useState<ProjectEstimation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const loadEstimations = async () => {
    try {
      setLoading(true);
      const response = await EstimationService.getClientEstimations({
        sort_by: 'created_at',
        sort_order: 'desc',
      });
      setEstimations(response.data);
      
      if (id) {
        const estimation = response.data.find(e => e.id === parseInt(id));
        if (estimation) {
          setSelectedEstimation(estimation);
          loadEstimationDetails(parseInt(id));
        }
      }
    } catch (err: any) {
      setError('Failed to load estimations');
    } finally {
      setLoading(false);
    }
  };

  const loadEstimationDetails = async (estimationId: number) => {
    try {
      const response = await EstimationService.getClientEstimation(estimationId);
      setSelectedEstimation(response.estimation as any);
    } catch (err) {
      console.error('Failed to load estimation details:', err);
    }
  };

  useEffect(() => {
    loadEstimations();
  }, [id]);

  if (showNewForm) {
    return (
      <Box sx={{ p: 3 }}>
        <ProjectSubmissionForm
          onSubmitSuccess={(estimationId) => {
            setShowNewForm(false);
            navigate(`/client/estimations/${estimationId}`);
            loadEstimations();
          }}
          onCancel={() => setShowNewForm(false)}
        />
      </Box>
    );
  }

  if (selectedEstimation) {
    return (
      <Box sx={{ p: 3 }}>
        <Button onClick={() => setSelectedEstimation(null)} sx={{ mb: 2 }}>
          ← Back to List
        </Button>
        
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h4">{selectedEstimation.project_name}</Typography>
            <Typography color="text.secondary" paragraph>
              {selectedEstimation.project_description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={selectedEstimation.status_label}
                color="primary"
              />
              <Chip label={`Progress: ${selectedEstimation.clarification_progress || 0}%`} />
            </Box>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Details" />
          <Tab label="Questions" />
          {selectedEstimation.status_label?.includes('complete') && (
            <Tab label="Specification" />
          )}
        </Tabs>

        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6">Project Details</Typography>
                <Typography paragraph>{selectedEstimation.project_description}</Typography>
              </CardContent>
            </Card>
          )}
          {activeTab === 1 && (
            <ClarificationQA
              estimationId={selectedEstimation.id}
              clarifications={selectedEstimation.clarifications || []}
              onAnswersSubmitted={() => loadEstimationDetails(selectedEstimation.id)}
            />
          )}
          {activeTab === 2 && selectedEstimation.latest_specification && (
            <SpecificationViewer
              specification={selectedEstimation.latest_specification}
              allVersions={selectedEstimation.specifications}
              estimationId={selectedEstimation.id}
            />
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">My Project Estimations</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowNewForm(true)}
        >
          New Estimation Request
        </Button>
      </Box>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      <Stack spacing={2}>
        {estimations.map((estimation) => (
          <Card
            key={estimation.id}
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate(`/client/estimations/${estimation.id}`)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6">{estimation.project_name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {estimation.project_description?.substring(0, 150)}...
                  </Typography>
                </Box>
                <Box>
                  <Chip label={estimation.status_label} size="small" />
                  {estimation.clarification_progress !== undefined && (
                    <Typography variant="caption" display="block">
                      {estimation.clarification_progress}% answered
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

export default ClientEstimationPage;
